import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Box from '@material-ui/core/Box'
import IconButton from '@material-ui/core/IconButton'
import { ListItemText } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { useIsTablet } from 'lib/hooks'
import FileCopyOutlinedIcon from 'components/material-symbol/icons/FileCopy'

const useLegacySectionStyles = makeStyles(theme => ({
  section: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column !important',
    },
  },
  sectionTitle: {
    fontWeight: '500',
    fontSize: '20px',
    margin: '24px 32px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column !important',
      margin: '16px 28px 8px',
      fontSize: '12px',
      lineHeight: '31px',
    },
  },
  sectionContent: {
    margin: '0px 16px',
    fontSize: '16px',
    lineHeight: '24px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column !important',
      margin: 0,
      padding: '0 16px',
      fontSize: '14px',
      lineHeight: '20px',
    },
  },
  listText: {
    wordBreak: 'break-word',
  },
}))

export const useInfoCardStyles = makeStyles(theme => ({
  card: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    minWidth: 0,
    height: '100%',
    boxSizing: 'border-box',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5, 3),
    borderBottom: '1px solid #E0E0E0',
  },
  iconCircle: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    '& .MuiSvgIcon-root': {
      fontSize: 16,
    },
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: 1.3,
    color: '#172024',
  },
  cardBody: {
    padding: theme.spacing(2.5, 3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
    margin: theme.spacing(0, -2),
    padding: theme.spacing(0.75, 2),
    borderRadius: 8,
    transition: 'background-color 0.15s ease',
  },
  contactRowInteractive: {
    '&:hover': {
      backgroundColor: '#F4F6F8',
    },
    '& $copyButton': {
      opacity: 0,
      pointerEvents: 'none',
      transition: 'opacity 0.15s ease',
    },
    '&:hover $copyButton, &:focus-within $copyButton': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  infoFieldRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  detailSection: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  detailSectionCompact: {
    marginTop: 0,
    marginBottom: 0,
  },
  detailSectionCard: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    boxSizing: 'border-box',
    height: '100%',
  },
  detailSectionCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 3),
    borderBottom: '1px solid #E0E0E0',
  },
  detailSectionCardTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: 1.3,
    color: '#172024',
  },
  detailSectionCardBody: {
    padding: theme.spacing(2.5, 3),
  },
  detailSectionCardHeaderPlain: {
    padding: theme.spacing(2.5, 3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2.5, 3),
    },
  },
  detailSectionCardHeaderCompact: {
    padding: theme.spacing(2, 2.5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5, 2),
    },
  },
  detailSectionCardBodyCompact: {
    padding: theme.spacing(2, 2.5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5, 2),
    },
  },
  detailSectionCardBodyPlain: {
    padding: theme.spacing(2.5, 3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2.5, 3),
    },
  },
  detailFieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: theme.spacing(3),
    rowGap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      columnGap: 0,
    },
  },
  detailFieldsStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  detailFieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
  },
  detailFieldIconCircle: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: '#E0EAFF',
    color: '#001C57',
    '& svg, & .MuiSvgIcon-root': {
      fontSize: 18,
      color: '#001C57',
    },
  },
  detailFieldContent: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  detailFieldLabel: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#172024',
    marginBottom: 2,
  },
  detailFieldValue: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.4,
    color: '#6B7C87',
    wordBreak: 'break-word',
  },
  detailFieldLink: {
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  fieldIcon: {
    fontSize: 18,
    color: theme.palette.text.secondary,
    marginTop: 2,
    flexShrink: 0,
  },
  fieldContent: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: 2,
  },
  fieldValue: {
    fontSize: '0.9375rem',
    fontWeight: 500,
    lineHeight: 1.3,
    color: '#172024',
    wordBreak: 'break-word',
  },
  fieldLink: {
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  copyButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    flexShrink: 0,
    alignSelf: 'center',
    '& .MuiSvgIcon-root': {
      fontSize: 18,
    },
  },
  aboutRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
  },
  aboutLabel: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  aboutValue: {
    fontSize: '0.9375rem',
    fontWeight: 500,
    lineHeight: 1.3,
    color: '#172024',
    textAlign: 'right',
    wordBreak: 'break-word',
  },
}))

export const ContentSection = ({ children, title = '', ...props }) => {
  const styles = useLegacySectionStyles()
  const isTablet = useIsTablet()

  return (
    <Card className={styles.section} variant="outlined" {...props}>
      <Typography className={styles.sectionTitle}>{title}</Typography>
      {!isTablet && <Divider />}
      <CardContent className={styles.sectionContent}>{children}</CardContent>
    </Card>
  )
}

export const ListText = ({ text, ...props }) => {
  const classes = useLegacySectionStyles()

  return (
    <ListItemText
      disableTypography
      className={classes.listText}
      primary={<Typography style={{ all: 'inherit' }}>{text}</Typography>}
      {...props}
    />
  )
}

export const InfoCard = ({ title, icon: Icon, children }) => {
  const classes = useInfoCardStyles()

  return (
    <Box className={classes.card}>
      <Box className={classes.cardHeader}>
        <Box className={classes.iconCircle}>
          <Icon />
        </Box>
        <Typography className={classes.cardTitle}>{title}</Typography>
      </Box>
      <Box className={classes.cardBody}>{children}</Box>
    </Box>
  )
}

export const DetailField = ({
  icon: Icon,
  label,
  value,
  href,
  onCopy,
  useRefreshedIcon = false,
  testId,
}) => {
  const classes = useInfoCardStyles()
  const rowClass = onCopy
    ? clsx(classes.contactRow, classes.contactRowInteractive)
    : classes.detailFieldRow

  const valueContent = href ? (
    <a
      href={href}
      className={`${classes.detailFieldValue} ${classes.detailFieldLink}`}
      data-testid={testId}
    >
      {value}
    </a>
  ) : (
    <Typography
      className={classes.detailFieldValue}
      component="span"
      data-testid={testId}
    >
      {value}
    </Typography>
  )

  return (
    <Box className={rowClass}>
      <Box className={classes.detailFieldIconCircle}>
        {useRefreshedIcon ? <Icon color="#001C57" size="18" /> : <Icon />}
      </Box>
      <Box className={classes.detailFieldContent}>
        <Typography className={classes.detailFieldLabel}>{label}</Typography>
        {valueContent}
      </Box>
      {onCopy && (
        <IconButton
          className={classes.copyButton}
          onClick={onCopy}
          size="small"
          aria-label={`Copy ${label.toLowerCase()}`}
        >
          <FileCopyOutlinedIcon />
        </IconButton>
      )}
    </Box>
  )
}

export const DetailSection = ({
  title,
  children,
  innerRef,
  compact = false,
  stacked = false,
  layout = 'grid',
  headerAction,
}) => {
  const classes = useInfoCardStyles()

  return (
    <Box
      className={clsx(
        classes.detailSection,
        compact && classes.detailSectionCompact,
      )}
      ref={innerRef}
    >
      <Box className={classes.detailSectionCard}>
        <Box
          className={clsx(
            classes.detailSectionCardHeader,
            compact && classes.detailSectionCardHeaderCompact,
            layout === 'plain' && classes.detailSectionCardHeaderPlain,
          )}
        >
          <Typography className={classes.detailSectionCardTitle} component="h2">
            {title}
          </Typography>
          {headerAction}
        </Box>
        <Box
          className={clsx(
            classes.detailSectionCardBody,
            compact && classes.detailSectionCardBodyCompact,
            layout === 'plain' && classes.detailSectionCardBodyPlain,
          )}
        >
          {layout === 'grid' ? (
            <Box
              className={
                stacked ? classes.detailFieldsStack : classes.detailFieldsGrid
              }
            >
              {children}
            </Box>
          ) : (
            children
          )}
        </Box>
      </Box>
    </Box>
  )
}

export const ContactField = ({
  icon: Icon,
  label,
  value,
  href,
  onCopy,
  testId,
}) => (
  <DetailField
    icon={Icon}
    label={label}
    value={value}
    href={href}
    onCopy={onCopy}
    testId={testId}
  />
)

export const InfoField = DetailField

export const AboutRow = ({ label, value }) => {
  const classes = useInfoCardStyles()

  if (!value) return null

  return (
    <Box className={classes.aboutRow}>
      <Typography className={classes.aboutLabel}>{label}</Typography>
      <Typography className={classes.aboutValue}>{value}</Typography>
    </Box>
  )
}
